// eslint-disable-next-line import/no-named-as-default
import posthog from 'posthog-js'
import Vue from 'vue'

export default ({ app, store, $config }, inject) => {
  if (!process.env.NODE_ENV.includes('production')) {
    return
  }

  if (typeof window === 'undefined') {
    return
  }

  const config = $config || app.$config || {}
  const apiKey = config.posthogApiKey || process.env.VUE_APP_POSTHOG_API_KEY
  const apiHost =
    config.posthogHost ||
    process.env.VUE_APP_POSTHOG_HOST ||
    'https://us.i.posthog.com'

  if (!apiKey) {
    return
  }

  const identifyUser = () => {
    try {
      const userStr = localStorage.getItem('user')
      if (!userStr) {
        posthog.reset()
        return
      }

      const user = typeof userStr === 'string' ? JSON.parse(userStr) : userStr
      if (user && user.id) {
        posthog.identify(user.id.toString(), {
          email: user.email,
          name: user.name,
          id: user.id,
          phone_number: user.phoneNumber || null,
        })
      }
    } catch (e) {}
  }

  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: true,
      capture_pageview: false,
      capture_exceptions: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          text: true,
          search: true,
          tel: true,
          url: true,
        },
      },
      loaded: (posthogInstance) => {
        if (process.env.NODE_ENV.includes('production')) {
          identifyUser()
          posthogInstance.capture('$pageview', {
            $current_url: window.location.href,
          })
        }
      },
    })
  } catch (error) {
    return
  }

  identifyUser()

  if (store && store.subscribe) {
    store.subscribe((mutation) => {
      if (
        mutation.type === 'auth/logout' ||
        mutation.type === 'logout' ||
        mutation.type === 'auth/LOGOUT'
      ) {
        posthog.reset()
      }
      if (
        mutation.type === 'SET_LOGGED_USER' ||
        mutation.type === 'auth/setUser' ||
        mutation.type === 'auth/SET_USER'
      ) {
        setTimeout(() => {
          identifyUser()
        }, 100)
      }
    })
  }

  window.addEventListener('storage', (e) => {
    if (e.key === 'user') {
      if (e.newValue) {
        setTimeout(() => {
          identifyUser()
        }, 100)
      } else {
        posthog.reset()
      }
    }
  })

  const normalizeError = (error) => {
    if (error instanceof Error) {
      return error
    }

    if (error?.error instanceof Error) {
      return error.error
    }

    const errorMessage = error?.message || String(error || 'Unknown error')
    const normalizedError = new Error(errorMessage)

    if (error?.stack && typeof error.stack === 'string') {
      normalizedError.stack = error.stack
    }

    return normalizedError
  }

  const ensureStackTrace = (error) => {
    if (error.stack) {
      return error
    }

    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(error)
      return error
    }

    const stackTrace = new Error('Stack trace').stack
    if (stackTrace) {
      error.stack = stackTrace
    }

    return error
  }

  const captureError = (error, context = {}) => {
    if (!error) {
      return
    }

    try {
      const normalizedError = normalizeError(error)
      const errorWithStack = ensureStackTrace(normalizedError)
      posthog.captureException(errorWithStack, context)
    } catch (e) {}
  }

  const originalErrorHandler = Vue.config.errorHandler
  Vue.config.errorHandler = (error, instance, info) => {
    captureError(error, {
      $exception_component:
        instance?.$options?.name || instance?.$options?.__file,
      $exception_context: info,
    })
    if (originalErrorHandler) {
      originalErrorHandler(error, instance, info)
    }
  }

  window.addEventListener('error', (event) => {
    captureError(event.error || new Error(event.message), {
      $exception_filename: event.filename,
      $exception_lineno: event.lineno,
      $exception_colno: event.colno,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason)
  })

  app.router.onError((error) => {
    captureError(error, {
      $exception_type: 'router.error',
      $exception_url: window.location.href,
    })
  })

  app.router.afterEach(() => {
    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
      })
    } catch (e) {}
  })

  inject('posthog', posthog)
  window.posthog = posthog
}
